﻿import React, { useState, useEffect } from "react";
import { getModelInputs } from "../../utils/companyApi.js";
import { loadUserData, saveUserData } from "../../utils/userStore.js";
import { fmtMoney, fmtNumber, fmtPct } from "../../utils/format.js";
import { Loading } from "./common.jsx";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const r1 = (v) => Math.round(v * 10) / 10;

export default function ModelTab({ ticker, user, onRequireLogin }) {
  const [base, setBase] = useState(null);
  const [error, setError] = useState("");
  const [m, setM] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let dead = false;
    setBase(null);
    setError("");
    getModelInputs(ticker)
      .then((d) => {
        if (dead) return;
        if (d.error) return setError("This company has no SEC financials to build a model from.");
        setBase(d);
        const mine = user ? (loadUserData("models", user, {}) || {})[ticker] : null;
        const margin = d.ltm.revenue > 0 ? (d.ltm.ni / d.ltm.revenue) * 100 : 10;
        const pe = d.ltm.eps > 0 ? d.price / d.ltm.eps : 15;
        setM(mine || {
          growth: r1(clamp(d.rev3yCagr === null ? 8 : d.rev3yCagr, 0, 25)),
          margin: r1(clamp(margin, 1, 60)),
          exitPe: Math.round(clamp(pe, 8, 30)),
          ret: 10,
          shares: 0,
          years: 5,
        });
      })
      .catch((e) => !dead && setError(e.message));
    return () => { dead = true; };
  }, [ticker]);

  if (error) return <div className="card co-card"><p className="co-hint">{error}</p></div>;
  if (!base || !m) return <Loading />;

  const set = (k) => (e) => { setSaved(false); setM({ ...m, [k]: parseFloat(e.target.value) || 0 }); };
  const rows = [];
  for (let y = 1; y <= m.years; y++) {
    const revenue = base.ltm.revenue * Math.pow(1 + m.growth / 100, y);
    const ni = revenue * (m.margin / 100);
    const sh = base.sharesMM * Math.pow(1 + m.shares / 100, y);
    rows.push({ y, revenue, ni, eps: ni / sh });
  }
  const last = rows[rows.length - 1];
  const futurePrice = last.eps * m.exitPe;
  const fair = futurePrice / Math.pow(1 + m.ret / 100, m.years);
  const upside = base.price ? (fair / base.price - 1) * 100 : null;

  const save = () => {
    if (!user) return onRequireLogin && onRequireLogin();
    saveUserData("models", user, { ...(loadUserData("models", user, {}) || {}), [ticker]: m });
    setSaved(true);
  };

  const Field = ({ k, label, step = "0.1" }) => (
    <div>
      <label className="tool-label">{label}</label>
      <input type="number" step={step} className="form-control" value={m[k]} onChange={set(k)} />
    </div>
  );

  return (
    <div>
      <div className="card co-card">
        <h6 className="co-card-title">Your assumptions for {ticker}</h6>
        <div className="tool-form" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
          <Field k="growth" label="Revenue growth / yr (%)" />
          <Field k="margin" label="Net margin (%)" />
          <Field k="exitPe" label="P/E at the end" step="1" />
          <Field k="ret" label="Return you want / yr (%)" />
          <Field k="shares" label="Share count change / yr (%)" />
          <Field k="years" label="Years" step="1" />
        </div>
        <p className="co-hint">
          Starting from real figures: revenue {fmtNumber(base.ltm.revenue, 0)} MM, net income {fmtNumber(base.ltm.ni, 0)} MM
          ({base.rev3yCagr === null ? "3-year growth n/a" : `${fmtPct(base.rev3yCagr, 1)} revenue growth over 3 years`}),
          price {fmtMoney(base.price)}, {fmtNumber(base.sharesMM, 0)} MM shares.
        </p>
      </div>

      <div className="card co-card">
        <h6 className="co-card-title">Result</h6>
        <dl className="co-stats">
          <div><dt>Fair value today</dt><dd>{fmtMoney(fair)}</dd></div>
          <div><dt>Current price</dt><dd>{fmtMoney(base.price)}</dd></div>
          <div><dt>Upside / downside</dt><dd className={upside >= 0 ? "change-up" : "change-down"}>{upside === null ? "—" : fmtPct(upside, 1, true)}</dd></div>
          <div><dt>Price in {m.years} years</dt><dd>{fmtMoney(futurePrice)}</dd></div>
        </dl>
        <button className="btn btn-purple" onClick={save}>{user ? (saved ? "Saved" : "Save this model") : "Log in to save"}</button>
      </div>

      <div className="card co-card co-table-wrap">
        <table className="co-table">
          <thead><tr><th className="co-sticky" />{rows.map((r) => <th key={r.y}>Year {r.y}</th>)}</tr></thead>
          <tbody>
            <tr><td className="co-sticky">Revenue ($MM)</td>{rows.map((r) => <td key={r.y}>{fmtNumber(r.revenue, 0)}</td>)}</tr>
            <tr><td className="co-sticky">Net income ($MM)</td>{rows.map((r) => <td key={r.y}>{fmtNumber(r.ni, 0)}</td>)}</tr>
            <tr className="bold"><td className="co-sticky">EPS ($)</td>{rows.map((r) => <td key={r.y}>{fmtNumber(r.eps, 2)}</td>)}</tr>
          </tbody>
        </table>
        <p className="co-hint">
          A simple earnings model: grow revenue, apply your margin, multiply the final EPS by your exit P/E, then discount back at the return you want.
          It is a tool for thinking, not a prediction or advice.
        </p>
      </div>
    </div>
  );
}
